import React, { useState } from 'react';
import { Eye, Sparkles, ChevronDown, ChevronUp, FileText, CheckCircle2, Copy, Check } from 'lucide-react';

export interface ParsedVisualContent {
  cleanCaption: string;
  extractedVision: string | null;
  extractedDoc: string | null;
  extractedSlip: any | null;
  hasExtracted: boolean;
}

export function parseMessageVisualContent(rawText?: string): ParsedVisualContent {
  if (!rawText || typeof rawText !== 'string') {
    return { cleanCaption: '', extractedVision: null, extractedDoc: null, extractedSlip: null, hasExtracted: false };
  }

  let text = rawText;
  let extractedVision: string | null = null;
  let extractedDoc: string | null = null;
  let extractedSlip: any | null = null;

  // 1. Detect and extract Bank Slip JSON tag
  const slipMatch = text.match(/\[PAYMENT_SLIP_JSON:\s*(\{.*?\})\]/s);
  if (slipMatch) {
    try {
      extractedSlip = JSON.parse(slipMatch[1]);
    } catch {}
    text = text.replace(/\[PAYMENT_SLIP_JSON:\s*\{.*?\}\]\s*/s, '');
  }

  // 2. Detect and extract Image Description & Visual Content
  const visionMarker = '[Image Description & Visual Content]:';
  const visionIdx = text.indexOf(visionMarker);
  if (visionIdx !== -1) {
    extractedVision = text.slice(visionIdx + visionMarker.length).trim();
    text = text.slice(0, visionIdx).trim();
  }

  // 3. Detect and extract Document Content / PDF Text
  const docMarker = '[Document Content / PDF Text]:';
  const docIdx = text.indexOf(docMarker);
  if (docIdx !== -1) {
    extractedDoc = text.slice(docIdx + docMarker.length).trim();
    text = text.slice(0, docIdx).trim();
  }

  // 4. Strip internal system instruction events from human-visible chat
  text = text.replace(/\[SYSTEM EVENT:.*?\]/gs, '').trim();

  return {
    cleanCaption: text,
    extractedVision,
    extractedDoc,
    extractedSlip,
    hasExtracted: Boolean(extractedVision || extractedDoc || extractedSlip),
  };
}

interface MessageVisualContentProps {
  text?: string;
  isIncoming?: boolean;
  messageKey?: string;
  hasImages?: boolean;
  theme?: 'whatsapp' | 'telemetry' | 'cyber';
}

export const MessageVisualContent: React.FC<MessageVisualContentProps> = ({
  text = '',
  isIncoming = true,
  messageKey = 'msg',
  hasImages = false,
  theme = 'whatsapp',
}) => {
  const [isVisionExpanded, setIsVisionExpanded] = useState<boolean>(false);
  const [isDocExpanded, setIsDocExpanded] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);

  const parsed = parseMessageVisualContent(text);

  const handleCopy = (content: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const isDarkTheme = theme === 'telemetry' || theme === 'cyber';

  return (
    <div className="space-y-2">
      {/* 1. Main Clean User Caption (if present) */}
      {parsed.cleanCaption ? (
        <div className={`whitespace-pre-wrap break-words font-sans selection:bg-[#00a884] selection:text-black ${
          isDarkTheme ? 'text-slate-100 text-xs leading-relaxed' : 'text-[#e9edef] text-[13px] leading-relaxed'
        }`}>
          {parsed.cleanCaption}
        </div>
      ) : !hasImages && parsed.extractedVision ? (
        <div className="flex items-center gap-1.5 text-xs text-slate-400 font-mono py-0.5">
          <Eye size={13} className="text-cyan-400" />
          <span>Image Attachment Ingested</span>
        </div>
      ) : null}

      {/* 2. Detected Bank Slip Metadata Card */}
      {parsed.extractedSlip && (
        <div className="rounded-xl border border-emerald-500/40 bg-emerald-950/40 p-2.5 shadow-sm text-xs font-mono space-y-1">
          <div className="flex items-center gap-1.5 text-emerald-400 font-bold">
            <CheckCircle2 size={14} />
            <span>Bank Payment Slip Verified</span>
          </div>
          <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 text-[11px] text-slate-300 pt-1 border-t border-emerald-500/20">
            {parsed.extractedSlip.bankName && <div><strong className="text-slate-400">Bank:</strong> {parsed.extractedSlip.bankName}</div>}
            {parsed.extractedSlip.amount && <div><strong className="text-slate-400">Amount:</strong> {parsed.extractedSlip.amount}</div>}
            {parsed.extractedSlip.referenceNumber && <div><strong className="text-slate-400">Ref:</strong> {parsed.extractedSlip.referenceNumber}</div>}
            {parsed.extractedSlip.senderName && <div><strong className="text-slate-400">Sender:</strong> {parsed.extractedSlip.senderName}</div>}
          </div>
        </div>
      )}

      {/* 3. Sleek Collapsible AI Extracted Vision / OCR Content Accordion */}
      {parsed.extractedVision && (
        <div className={`mt-2 rounded-xl border transition-all duration-200 overflow-hidden shadow-md ${
          isDarkTheme
            ? 'border-cyan-500/30 bg-[#070D1E]/90'
            : 'border-white/15 bg-black/40'
        }`}>
          <button
            type="button"
            onClick={() => setIsVisionExpanded((prev) => !prev)}
            className={`w-full px-3 py-2 flex items-center justify-between gap-2 text-left transition cursor-pointer ${
              isDarkTheme
                ? 'hover:bg-cyan-500/10 text-cyan-300'
                : 'hover:bg-white/5 text-cyan-300'
            }`}
          >
            <div className="flex items-center gap-2 text-xs font-bold truncate">
              <Sparkles size={13} className="text-cyan-400 shrink-0 animate-pulse" />
              <span className="truncate">AI Extracted Visual Content & OCR</span>
              <span className="px-1.5 py-0.5 rounded text-[9px] font-mono bg-cyan-500/20 text-cyan-200 border border-cyan-500/40 shrink-0">
                {isVisionExpanded ? 'Collapse' : 'Expand Details'}
              </span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {isVisionExpanded && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => handleCopy(parsed.extractedVision!, e)}
                  className="p-1 rounded hover:bg-cyan-500/20 text-slate-400 hover:text-cyan-300 transition"
                  title="Copy extracted visual content"
                >
                  {copied ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
                </span>
              )}
              {isVisionExpanded ? (
                <ChevronUp size={14} className="text-cyan-400 shrink-0" />
              ) : (
                <ChevronDown size={14} className="text-cyan-400 shrink-0" />
              )}
            </div>
          </button>

          {isVisionExpanded && (
            <div className={`p-3 border-t text-[11px] font-sans leading-relaxed whitespace-pre-wrap max-h-80 overflow-y-auto ${
              isDarkTheme
                ? 'border-cyan-500/20 bg-slate-950/80 text-slate-200 selection:bg-cyan-500/30'
                : 'border-white/10 bg-black/60 text-slate-200 selection:bg-teal-500/40'
            }`}>
              {parsed.extractedVision}
            </div>
          )}
        </div>
      )}

      {/* 4. Sleek Collapsible AI Extracted Document / PDF Content Accordion */}
      {parsed.extractedDoc && (
        <div className={`mt-2 rounded-xl border transition-all duration-200 overflow-hidden shadow-md ${
          isDarkTheme
            ? 'border-rose-500/30 bg-[#140A18]/90'
            : 'border-white/15 bg-black/40'
        }`}>
          <button
            type="button"
            onClick={() => setIsDocExpanded((prev) => !prev)}
            className="w-full px-3 py-2 flex items-center justify-between gap-2 text-left hover:bg-rose-500/10 transition cursor-pointer text-rose-300"
          >
            <div className="flex items-center gap-2 text-xs font-bold truncate">
              <FileText size={13} className="text-rose-400 shrink-0" />
              <span className="truncate">AI Extracted Document & Fact Summary</span>
              <span className="px-1.5 py-0.5 rounded text-[9px] font-mono bg-rose-500/20 text-rose-200 border border-rose-500/40 shrink-0">
                {isDocExpanded ? 'Collapse' : 'Expand'}
              </span>
            </div>
            {isDocExpanded ? (
              <ChevronUp size={14} className="text-rose-400 shrink-0" />
            ) : (
              <ChevronDown size={14} className="text-rose-400 shrink-0" />
            )}
          </button>

          {isDocExpanded && (
            <div className="p-3 border-t border-rose-500/20 bg-slate-950/80 text-[11px] font-sans leading-relaxed whitespace-pre-wrap max-h-80 overflow-y-auto text-slate-200">
              {parsed.extractedDoc}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
